import { useEffect, useId, useRef } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { IconButton } from './Button';
import { selectListIsOpen } from './Controls';

/**
 * Dialog and Drawer: the one overlay of the dashboard (CO-88).
 *
 * Both are a native <dialog> opened with showModal(), so the browser traps
 * focus, makes the page behind inert and turns Escape into `cancel`. On top
 * of that this component guarantees what four hand-built overlay families
 * did not (audit O1, O2, O4):
 *
 * - one layout: title and an optional one-line description, a body that
 *   scrolls on its own, and a footer that always stays visible, with its
 *   actions right-aligned in the order [Cancel] [Primary];
 * - one close control, a labelled ✕ icon button, in every dialog except a
 *   confirmation (which has Cancel);
 * - focus returns to the control that opened it when it closes;
 * - fixed widths: sm 420, md 560, lg 720; drawers 560 or 720 (wide).
 *
 * Render it with `open`; closed, it renders nothing.
 */

export type DialogSize = 'sm' | 'md' | 'lg';

const FOCUSABLE =
  'a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])';

interface OverlayProps {
  open: boolean;
  title: ReactNode;
  /** One line under the title. Longer explanations belong in the body. */
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /** Buttons for the sticky footer, in reading order: [Cancel] [Primary]. */
  footer?: ReactNode;
  /** When set, body and footer are one <form>, so Enter submits. */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  /** While true, Escape, the backdrop and ✕ do not close it. */
  busy?: boolean;
  /** Confirmations have Cancel instead of a ✕. */
  hideClose?: boolean;
  testId?: string;
  className?: string;
}

function Overlay({
  variant,
  size,
  open,
  title,
  description,
  onClose,
  children,
  footer,
  onSubmit,
  busy = false,
  hideClose = false,
  testId,
  className,
}: OverlayProps & { variant: 'dialog' | 'drawer'; size: string }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!open) return;
    const dialog = ref.current;
    // Remember who opened it: the browser does not reliably hand focus
    // back when a React-rendered dialog unmounts.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (dialog && !dialog.open) {
      dialog.showModal();
      // showModal() moves focus to the first focusable element (the close
      // button); a form that starts with a field marks it data-autofocus.
      dialog.querySelector<HTMLElement>('[data-autofocus]')?.focus();
    }
    return () => {
      if (dialog?.open) dialog.close();
      if (opener && opener.isConnected) opener.focus();
    };
  }, [open]);

  if (!open) return null;

  const close = () => {
    if (!busy) onClose();
  };

  const body = (
    <>
      <div className="dialog__body">{children}</div>
      {footer ? <footer className="dialog__footer">{footer}</footer> : null}
    </>
  );

  return (
    <dialog
      ref={ref}
      className={`dialog dialog--${variant} dialog--${size}${className ? ` ${className}` : ''}`}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      data-testid={testId}
      onCancel={(event) => {
        // Escape is a cancel, never a confirm or a save.
        event.preventDefault();
        close();
      }}
      onKeyDown={(event) => {
        // Some embedded and automated contexts deliver Escape without the
        // browser's cancel step; closing twice is a no-op.
        if (event.key === 'Escape') {
          // An open select list takes this Escape itself; the next one closes the dialog.
          if (selectListIsOpen(event.target)) return;
          event.preventDefault();
          close();
          return;
        }
        // A modal <dialog> makes the page inert, but Tab from its last
        // control still leaves for the browser's own UI. Keep the cycle
        // inside the dialog.
        if (event.key === 'Tab' && ref.current) {
          const focusable = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
            (element) => !element.hasAttribute('disabled') && element.getClientRects().length > 0,
          );
          if (focusable.length === 0) return;
          const first = focusable[0]!;
          const last = focusable[focusable.length - 1]!;
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }}
      onClick={(event) => {
        // A click on the backdrop lands on the <dialog> element itself.
        if (event.target === ref.current) close();
      }}
    >
      <header className="dialog__header">
        <div className="dialog__heading">
          <h2 id={titleId} className="dialog__title">
            {title}
          </h2>
          {description ? (
            <p id={descriptionId} className="dialog__description">
              {description}
            </p>
          ) : null}
        </div>
        {hideClose ? null : <IconButton icon="close" label="Close" onClick={close} disabled={busy} variant="ghost" />}
      </header>
      {onSubmit ? (
        <form className="dialog__form" onSubmit={onSubmit} noValidate={false}>
          {body}
        </form>
      ) : (
        body
      )}
    </dialog>
  );
}

export function Dialog({ size = 'md', ...props }: OverlayProps & { size?: DialogSize }) {
  return <Overlay variant="dialog" size={size} {...props} />;
}

/** A panel pinned to the right edge for configuration that belongs to a
 * page but does not need to sit on it permanently. */
export function Drawer({ size = 'default', ...props }: OverlayProps & { size?: 'default' | 'wide' }) {
  return <Overlay variant="drawer" size={size} {...props} />;
}
